import { iconNavDots } from '@bsds-lib-component/icons/icons';
import { BsdsCheckbox, BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useId, useRef, useState } from 'react';

import type { ParticipantTableItem, ParticipantTableProps } from '../../../types/previdencia/table/participantTableType';
import { formatCpf } from '../../../utils/cpfUtils';
import { generateParticipantsXLSX } from '../../../utils/participantExcelUtils';
import { generateParticipantPDF } from '../../../utils/participantPdfUtils';
import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from '../shared/TableMenuPortal';
import { TABLE_MENU_ITEM_BUTTON_CLASS } from '../shared/tableMenuUtils';

const ParticipantTable: React.FC<ParticipantTableProps> = ({
  participants,
  onRowClick,
  emptyMessage = 'Nenhum participante encontrado',
  onSelectionChange,
  onExport,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openItemMenuId, setOpenItemMenuId] = useState<string | number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const emptyStateRef = useRef<HTMLTableCellElement>(null);
  const itemMenuRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const itemExportButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const menuButtonId = useId();

  const allSelected = participants.length > 0 && selectedIds.size === participants.length;
  const hasSelectedItems = selectedIds.size > 0;

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Close header menu
      if (
        !menuRef.current?.contains(target)
        && !menuButtonRef.current?.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleItemMenuClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Close item menus using click event (fires after mousedown, allowing onClick to process)
      if (openItemMenuId !== null) {
        const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        // If click was inside the menu (which includes the export button), don't close
        if (
          itemMenuRef
          && !itemMenuRef.contains(target)
          && !itemMenuButtonRef?.contains(target)
        ) {
          setOpenItemMenuId(null);
        }
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMenuOpen) {
          setIsMenuOpen(false);
          menuButtonRef.current?.focus();
        }
        if (openItemMenuId !== null) {
          const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
          setOpenItemMenuId(null);
          itemMenuButtonRef?.focus();
        }
      }
    };

    // Use mousedown for header menu (standard behavior)
    document.addEventListener('mousedown', handleClickOutside);
    // Use click for item menus (fires after onClick, allowing it to process first)
    document.addEventListener('click', handleItemMenuClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleItemMenuClickOutside);
      document.removeEventListener('keydown', handleEscape as unknown as EventListener);
    };
  }, [isMenuOpen, openItemMenuId]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectedIds));
    }
  }, [selectedIds, onSelectionChange]);

  useEffect(() => {
    if (participants.length === 0) {
      emptyStateRef.current?.focus();
    }
  }, [participants.length]);

  useEffect(() => {
    if (isMenuOpen) {
      exportButtonRef.current?.focus();
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (openItemMenuId !== null) {
      const itemExportButtonRef = itemExportButtonRefs.current.get(openItemMenuId);
      itemExportButtonRef?.focus();
    }
  }, [openItemMenuId]);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set<string | number>());
    } else {
      const allIds = new Set<string | number>(participants.map((participant: ParticipantTableItem) => participant.id));
      setSelectedIds(allIds);
    }
  };

  const handleSelectItem = (id: string | number) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleRowClick = (participant: ParticipantTableItem, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('bsds-checkbox')) {
      return;
    }
    onRowClick?.(participant);
  };

  const handleExport = async () => {
    if (!hasSelectedItems) {
      return;
    }

    // Get the complete items based on the selected IDs
    const selectedParticipants = participants.filter((participant: ParticipantTableItem) =>
      selectedIds.has(participant.id)
    );

    // If onExport callback is provided, use it (for backward compatibility)
    if (onExport) {
      onExport(selectedParticipants);
    } else {
      // Otherwise, use the built-in XLSX export
      await generateParticipantsXLSX(selectedParticipants, formatCpf);
    }

    setIsMenuOpen(false);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isMenuOpen) {
        setIsMenuOpen(true);
      } else {
        exportButtonRef.current?.focus();
      }
    } else if (event.key === 'Escape' && isMenuOpen) {
      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleExportKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleExport();
    } else if (event.key === 'Escape') {
      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleRowKeyDown = (participant: ParticipantTableItem, event: React.KeyboardEvent) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick(participant);
    }
  };

  const handleItemMenuToggle = (participantId: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId(openItemMenuId === participantId ? null : participantId);
  };

  const handleItemMenuKeyDown = (participantId: string | number, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(openItemMenuId === participantId ? null : participantId);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (openItemMenuId !== participantId) {
        setOpenItemMenuId(participantId);
      } else {
        const itemExportButtonRef = itemExportButtonRefs.current.get(participantId);
        itemExportButtonRef?.focus();
      }
    } else if (event.key === 'Escape' && openItemMenuId === participantId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(participantId);
      itemMenuButtonRef?.focus();
    }
  };

  const handleExportPdf = (participant: ParticipantTableItem, event: React.MouseEvent) => {
    event.stopPropagation();
    generateParticipantPDF(participant, formatCpf);
    setOpenItemMenuId(null);
  };

  const handleExportPdfKeyDown = (participant: ParticipantTableItem, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      generateParticipantPDF(participant, formatCpf);
      setOpenItemMenuId(null);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(participant.id);
      itemMenuButtonRef?.focus();
    }
  };

  return (
    <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
      <div>
        <table className="min-w-[800px] w-full table-fixed" aria-label="Tabela de participantes">
          <thead>
            <tr>
              <th
                className="px-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16"
                scope="col"
              >
                <Tooltip label="Ações da tabela">
                  <div className="flex items-center gap-1">
                    <Tooltip label="Menu de ações">
                      <button
                        type="button"
                        id={menuButtonId}
                        ref={menuButtonRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(!isMenuOpen);
                        }}
                        onKeyDown={handleMenuKeyDown}
                        className="hover:bg-gray-100 focus-visible:outline  focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors"
                        aria-label="Menu de ações"
                        aria-expanded={isMenuOpen}
                        aria-haspopup="menu"
                        aria-controls={`${menuButtonId}-menu`}
                        aria-pressed={isMenuOpen}
                      >
                        <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <TableMenuPortal
                      isOpen={isMenuOpen}
                      anchorRef={menuButtonRef}
                      menuRef={menuRef}
                      align="left"
                      id={`${menuButtonId}-menu`}
                      aria-labelledby={menuButtonId}
                      aria-label="Ações da tabela"
                      className="w-40"
                    >
                      <button
                        type="button"
                        ref={exportButtonRef}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExport();
                        }}
                        onKeyDown={handleExportKeyDown}
                        disabled={!hasSelectedItems}
                        role="menuitem"
                        aria-disabled={!hasSelectedItems}
                        className={`${TABLE_MENU_ITEM_BUTTON_CLASS} ${hasSelectedItems
                          ? ''
                          : 'text-gray-400 cursor-not-allowed opacity-50'
                          }`}
                      >
                        Exportar
                      </button>
                    </TableMenuPortal>
                    <Tooltip label="Selecionar todos os participantes">
                      <div className="pl-2">
                        <BsdsCheckbox
                          checked={allSelected}
                          onBsdsChange={handleSelectAll}
                          size="small"
                          color="secondary"
                          aria-label="Selecionar todos os participantes"
                        />
                      </div>
                    </Tooltip>
                  </div>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Nome do Participante">
                  <span>Nome do Participante</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="CPF">
                  <span>CPF</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Número de Matrícula">
                  <span>Número de Matrícula</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Número do Contrato">
                  <span>Número do Contrato</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Empresa">
                  <span>Empresa</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações">
                  <span>Ações</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 text-text-color">
            {participants && participants.length > 0 ? (
              participants.map((participant: ParticipantTableItem) => {
                const isSelected = selectedIds.has(participant.id);

                return (
                  <tr
                    key={participant.id}
                    className={`hover:bg-gray-50 focus-visible:outline  focus-visible:outline-offset-2 focus-visible:outline-gray-500 transition-colors text-xs ${onRowClick ? 'cursor-pointer' : ''
                      }`}
                    onClick={(e) => handleRowClick(participant, e)}
                    onKeyDown={(e) => handleRowKeyDown(participant, e)}
                    aria-selected={isSelected}
                    aria-label={
                      onRowClick
                        ? `Abrir detalhes do participante ${participant.participantName}, CPF ${participant.cpf}`
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                  >
                    <td className="px-8 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label={`Selecionar participante`}>
                        <BsdsCheckbox
                          checked={isSelected}
                          onBsdsChange={() => handleSelectItem(participant.id)}
                          size="small"
                          color="secondary"
                          aria-label={`Selecionar participante ${participant.participantName}, CPF ${participant.cpf}`}
                        />
                      </Tooltip>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{participant.participantName || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatCpf(participant.cpf) || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{participant.registrationNumber || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{participant.contractNumber || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{participant.companyName || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Ações do participante">
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) {
                              itemMenuButtonRefs.current.set(participant.id, el);
                            } else {
                              itemMenuButtonRefs.current.delete(participant.id);
                            }
                          }}
                          onClick={(e) => handleItemMenuToggle(participant.id, e)}
                          onKeyDown={(e) => handleItemMenuKeyDown(participant.id, e)}
                          className="hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors p-1"
                          aria-label={`Ações do participante ${participant.participantName}`}
                          aria-expanded={openItemMenuId === participant.id}
                          aria-haspopup="menu"
                          aria-controls={`participant-menu-${participant.id}`}
                          aria-pressed={openItemMenuId === participant.id}
                        >
                          <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                        </button>
                      </Tooltip>
                      <TableMenuPortal
                        isOpen={openItemMenuId === participant.id}
                        anchorElement={itemMenuButtonRefs.current.get(participant.id) ?? null}
                        menuRef={(el) => {
                          if (el) {
                            itemMenuRefs.current.set(participant.id, el);
                          } else {
                            itemMenuRefs.current.delete(participant.id);
                          }
                        }}
                        align="right"
                        id={`participant-menu-${participant.id}`}
                        className="w-40"
                        aria-label={`Menu de ações do participante ${participant.participantName}`}
                      >
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) {
                              itemExportButtonRefs.current.set(participant.id, el);
                            } else {
                              itemExportButtonRefs.current.delete(participant.id);
                            }
                          }}
                          onClick={(e) => handleExportPdf(participant, e)}
                          onKeyDown={(e) => handleExportPdfKeyDown(participant, e)}
                          role="menuitem"
                          className={TABLE_MENU_ITEM_BUTTON_CLASS}
                        >
                          Exportar PDF
                        </button>
                      </TableMenuPortal>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  ref={emptyStateRef}
                  tabIndex={-1}
                  colSpan={7}
                  className="px-4 py-4 text-left sm:text-center text-text-color text-xs focus-visible:outline  focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParticipantTable;

